/**
 * Client-Side Projection Engine
 *
 * Pure functions — no side effects, no API calls, no state.
 * Same formulas as backend/app/services/projection_engine.py.
 *
 * IF YOU CHANGE A FORMULA HERE, CHANGE IT IN THE BACKEND TOO.
 */

export interface BaseActuals {
  revenue: number
  cogs: number
  operating_expenses: number
  total_expenses: number
  net_income: number
  total_assets: number
  current_assets: number
  ar_balance: number
  total_liabilities: number
  current_liabilities: number
  ap_balance: number
  total_equity: number
  working_capital: number
  cash?: number
}

export interface Assumptions {
  revenue_growth_rate: number      // % (e.g., 8.0 means 8%)
  cogs_pct_of_revenue: number      // % (e.g., 65.0)
  opex_growth_rate: number         // %
  headcount_change: number         // count
  cost_per_head: number            // $ annual
  collection_days: number          // days
  payment_terms: number            // days
  interest_rate: number            // %
  tax_rate: number                 // %
  capex: number                    // $
  depreciation: number             // $
}

export interface ProjectedPL {
  revenue: number
  cogs: number
  gross_profit: number
  gross_margin: number
  operating_expenses: number
  operating_income: number
  interest_expense: number
  pre_tax_income: number
  tax_expense: number
  net_income: number
  net_margin: number
}

export interface ProjectedBS {
  total_assets: number
  current_assets: number
  ar_balance: number
  total_liabilities: number
  current_liabilities: number
  ap_balance: number
  total_equity: number
  working_capital: number
  current_ratio: number
}

export interface ProjectedCF {
  starting_cash: number
  operating_cf: number
  investing_cf: number
  financing_cf: number
  ending_cash: number
}

export interface ProjectedKPIs {
  dso: number
  dpo: number
  cash_conversion_cycle: number
  gross_margin: number
  net_margin: number
  current_ratio: number
}

export interface ProjectionResult {
  pl: ProjectedPL
  bs: ProjectedBS
  cf: ProjectedCF
  kpis: ProjectedKPIs
  warnings: string[]
}

export function project(actuals: BaseActuals, assumptions: Assumptions): ProjectionResult {
  const warnings: string[] = []

  // Convert percentages
  const revenueGrowth = assumptions.revenue_growth_rate / 100
  const cogsPct = assumptions.cogs_pct_of_revenue / 100
  const opexGrowth = assumptions.opex_growth_rate / 100
  const interestRate = assumptions.interest_rate / 100
  const taxRate = assumptions.tax_rate / 100

  // -- P&L --
  const revenue = actuals.revenue * (1 + revenueGrowth)
  const cogs = revenue * cogsPct
  const gross_profit = revenue - cogs
  const gross_margin = revenue !== 0 ? (gross_profit / revenue) * 100 : 0

  let operating_expenses = actuals.operating_expenses * (1 + opexGrowth)
  const headcount_cost = (assumptions.headcount_change * assumptions.cost_per_head) / 12
  operating_expenses += headcount_cost

  const operating_income = gross_profit - operating_expenses
  const long_term_debt = actuals.total_liabilities - actuals.current_liabilities
  const interest_expense = (long_term_debt * interestRate) / 12
  const pre_tax_income = operating_income - interest_expense
  const tax_expense = Math.max(0, pre_tax_income * taxRate)
  const net_income = pre_tax_income - tax_expense
  const net_margin = revenue !== 0 ? (net_income / revenue) * 100 : 0

  const pl: ProjectedPL = {
    revenue: r(revenue),
    cogs: r(cogs),
    gross_profit: r(gross_profit),
    gross_margin: r1(gross_margin),
    operating_expenses: r(operating_expenses),
    operating_income: r(operating_income),
    interest_expense: r(interest_expense),
    pre_tax_income: r(pre_tax_income),
    tax_expense: r(tax_expense),
    net_income: r(net_income),
    net_margin: r1(net_margin),
  }

  // -- BS --
  const ar_balance = revenue * (assumptions.collection_days / 365)
  const ap_balance = cogs * (assumptions.payment_terms / 365)
  const projected_assets = actuals.total_assets + net_income + assumptions.capex - assumptions.depreciation
  const current_assets = actuals.current_assets + (ar_balance - actuals.ar_balance)
  const current_liabilities = actuals.current_liabilities + (ap_balance - actuals.ap_balance)
  const total_equity = actuals.total_equity + net_income
  const total_liabilities = projected_assets - total_equity
  const working_capital = current_assets - current_liabilities
  const current_ratio = current_liabilities !== 0 ? current_assets / current_liabilities : 0

  const bs: ProjectedBS = {
    total_assets: r(projected_assets),
    current_assets: r(current_assets),
    ar_balance: r(ar_balance),
    total_liabilities: r(total_liabilities),
    current_liabilities: r(current_liabilities),
    ap_balance: r(ap_balance),
    total_equity: r(total_equity),
    working_capital: r(working_capital),
    current_ratio: r2(current_ratio),
  }

  // -- CF --
  const delta_ar = ar_balance - actuals.ar_balance
  const delta_ap = ap_balance - actuals.ap_balance
  const starting_cash = actuals.cash ?? actuals.current_assets * 0.3
  const operating_cf = net_income + assumptions.depreciation - delta_ar + delta_ap
  const investing_cf = -assumptions.capex
  const financing_cf = 0
  const ending_cash = starting_cash + operating_cf + investing_cf + financing_cf

  const cf: ProjectedCF = {
    starting_cash: r(starting_cash),
    operating_cf: r(operating_cf),
    investing_cf: r(investing_cf),
    financing_cf: r(financing_cf),
    ending_cash: r(ending_cash),
  }

  // -- KPIs --
  const kpis: ProjectedKPIs = {
    dso: assumptions.collection_days,
    dpo: assumptions.payment_terms,
    cash_conversion_cycle: assumptions.collection_days - assumptions.payment_terms,
    gross_margin: r1(gross_margin),
    net_margin: r1(net_margin),
    current_ratio: r2(current_ratio),
  }

  // -- Warnings --
  if (ending_cash < 0) warnings.push('Projected cash is negative -- review revenue assumptions or reduce expenses')
  if (net_income < 0) warnings.push(`Projected net loss of $${Math.abs(net_income).toLocaleString()}`)
  if (current_ratio < 1.0) warnings.push(`Current ratio ${current_ratio.toFixed(1)}x below 1.0 -- liquidity risk`)
  if (assumptions.collection_days > 60) warnings.push(`DSO at ${assumptions.collection_days} days -- collection efficiency concern`)

  return { pl, bs, cf, kpis, warnings }
}

/** 13-week cash forecast with weekly granularity and confidence bands. */
export function cashForecast13Week(actuals: BaseActuals, assumptions: Assumptions): Array<{
  week: number; starting: number; inflows: number; outflows: number;
  ending: number; confidence_low: number; confidence_high: number;
}> {
  const proj = project(actuals, assumptions)
  let cash = proj.cf.starting_cash
  const weeklyInflow = proj.pl.revenue / 4.33
  const weeklyOutflow = (proj.pl.cogs + proj.pl.operating_expenses) / 4.33
  const spread = 0.02

  return Array.from({ length: 13 }, (_, i) => {
    const w = i + 1
    const ending = cash + weeklyInflow - weeklyOutflow
    const s = Math.abs(ending) * spread * w
    const row = {
      week: w,
      starting: r(cash),
      inflows: r(weeklyInflow),
      outflows: r(weeklyOutflow),
      ending: r(ending),
      confidence_low: r(ending - s),
      confidence_high: r(ending + s),
    }
    cash = ending
    return row
  })
}

// Rounding helpers — match Python's round(v, N)
function r(v: number): number { return Math.round(v * 100) / 100 }
function r1(v: number): number { return Math.round(v * 10) / 10 }
function r2(v: number): number { return Math.round(v * 100) / 100 }
